// Khung trang văn bản pháp lý công khai (không cần đăng nhập): Chính sách quyền riêng tư, Điều khoản sử dụng.
// Nội dung đọc từ markdown của pháp chế (src/content/legal/*.md, qua lib/legalDoc) — trang này chỉ lo hình:
// thanh trên (Quay lại · In), tiêu đề + ngày hiệu lực, mục lục bên trái tự sáng theo vị trí cuộn.

import * as React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, FileText, Printer, ScrollText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ShopTrackLogo } from '@/components/ui/logo'
import type { LegalBlock, LegalDoc, LegalInline, LegalList } from '@/lib/legalDoc'

function Inline({ runs }: { runs: LegalInline[] }) {
  return (
    <>
      {runs.map((r, i) => {
        if (r.href) {
          return (
            <a key={i} href={r.href} className="text-accent underline underline-offset-2">
              {r.text}
            </a>
          )
        }
        if (r.bold) return <strong key={i} className="font-semibold text-ink">{r.text}</strong>
        if (r.italic) return <em key={i} className="font-medium not-italic text-ink">{r.text}</em>
        return <React.Fragment key={i}>{r.text}</React.Fragment>
      })}
    </>
  )
}

/** Số mục: "5.1" khi ở danh sách cấp 1 của mục có số (Điều khoản dẫn chiếu "Mục 5.5"), "1)" / "1." còn lại. */
function marker(list: LegalList, n: number | undefined, section: number | undefined, nested: boolean): string {
  if (list.style === 'bullet' || n === undefined) return '•'
  if (list.style === 'paren') return `${n})`
  return section !== undefined && !nested ? `${section}.${n}` : `${n}.`
}

function List({ list, section, nested = false }: { list: LegalList; section?: number; nested?: boolean }) {
  return (
    <ol className={nested ? 'mt-2 space-y-1.5' : 'space-y-2.5'}>
      {list.items.map((item, i) => (
        <li key={i} className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2.5">
          <span className="min-w-6 tabular-nums font-medium text-inkSoft">{marker(list, item.number, section, nested)}</span>
          <div>
            <Inline runs={item.inline} />
            {item.children && <List list={item.children} nested />}
          </div>
        </li>
      ))}
    </ol>
  )
}

function Blocks({ blocks, section }: { blocks: LegalBlock[]; section?: number }) {
  return (
    <>
      {blocks.map((b, i) =>
        b.type === 'paragraph' ? (
          <p key={i}>
            <Inline runs={b.inline} />
          </p>
        ) : (
          <List key={i} list={b} section={section} />
        ),
      )}
    </>
  )
}

const sectionLabel = (s: LegalDoc['sections'][number]) => (s.number !== undefined ? `${s.number}. ${s.title}` : s.title)

export function LegalDocumentPage({
  doc,
  back,
  related,
}: {
  doc: LegalDoc
  back: { to: string; label: string }
  /** Văn bản đi kèm (Chính sách ↔ Điều khoản), link ở cuối trang. */
  related: { to: string; label: string }
}) {
  const [activeSec, setActiveSec] = React.useState(doc.sections[0]?.id ?? '')

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSec(entry.target.id)
            break
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px' },
    )
    doc.sections.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [doc])

  function scrollTo(id: string) {
    const el = document.getElementById(id)
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' })
  }

  return (
    <div className="min-h-screen bg-paper text-ink selection:bg-accent/20">
      <header className="sticky top-0 z-30 border-b border-rule bg-paper/90 backdrop-blur-md px-4 sm:px-6 py-3.5 print:hidden">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <Link to={back.to} className="inline-flex items-center gap-1.5 text-xs text-inkSoft hover:text-ink transition-colors shrink-0">
              <ArrowLeft className="size-4" />
              <span>{back.label}</span>
            </Link>
            <div className="h-4 w-px bg-rule" />
            <Link to="/" className="inline-block hover:opacity-90 transition-opacity">
              <ShopTrackLogo className="text-accent" textClassName="text-lg font-bold text-ink" brandable={false} />
            </Link>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.print()} className="text-xs gap-1.5">
            <Printer className="size-3.5" />
            <span>In văn bản</span>
          </Button>
        </div>
      </header>

      <div className="border-b border-rule bg-gradient-to-b from-raise/60 to-paper py-10 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {doc.effective && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rule bg-raise text-xs text-inkSoft mb-4">
              <ScrollText className="size-3.5 text-accent" />
              <span>{doc.effective}</span>
            </div>
          )}
          <h1 className="text-2xl sm:text-4xl font-bold text-ink tracking-tight">{doc.title}</h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[280px_1fr] gap-12">
        <aside className="hidden lg:block print:hidden">
          <div className="sticky top-24 space-y-4 max-h-[calc(100vh-7rem)] overflow-y-auto pb-4">
            <div className="flex items-center gap-2 pb-2 border-b border-rule text-xs font-semibold text-inkSoft">
              <FileText className="size-4 text-accent" />
              <span>Mục lục</span>
            </div>
            <nav className="space-y-1 text-xs">
              {doc.sections.map((sec) => (
                <button
                  key={sec.id}
                  onClick={() => scrollTo(sec.id)}
                  className={`w-full text-left px-3 py-2 rounded-md transition-all truncate block ${
                    activeSec === sec.id ? 'bg-accent/10 text-accent font-semibold' : 'text-inkSoft hover:bg-raise hover:text-ink'
                  }`}
                  title={sectionLabel(sec)}
                >
                  {sectionLabel(sec)}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        <main className="max-w-none text-ink space-y-12 text-sm leading-relaxed">
          {doc.intro.length > 0 && (
            <div className="space-y-3">
              <Blocks blocks={doc.intro} />
            </div>
          )}
          {doc.sections.map((sec) => (
            <section key={sec.id} id={sec.id} className="scroll-mt-24 space-y-3">
              <h2 className="text-xl font-bold text-ink border-b border-rule pb-2">{sectionLabel(sec)}</h2>
              <Blocks blocks={sec.blocks} section={sec.number} />
            </section>
          ))}
          <p className="text-inkSoft print:hidden">
            Xem thêm{' '}
            <Link to={related.to} className="text-accent underline">
              {related.label}
            </Link>
            .
          </p>
        </main>
      </div>
    </div>
  )
}
